import { User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useAddMember } from "@/api/user/add-new-member";
import useOnboardingData from "@/store/onboarding-data";
import PhoneInput from "react-phone-number-input";
import { useGetMembers } from "@/api/user/get-members";
import { Badge } from "@/components/ui/badge";
import { useEffect, useState } from "react";
import { useDeleteMember } from "@/api/user/delete-members";
import { useEditMember } from "@/api/user/edit-member";
import { Separator } from "@/components/ui/separator";
import useGlobalPermission from "@/store/user-permissions";
import { useUpdatePermission } from "@/api/permissions/update-permission";

type MemberType = {
    id: string;
    name: string;
    email: string;
    phone: string;
    avatar: string;
    role: "member" | "owner";
    is_active: boolean;
};

const Members = () => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editData, setEditData] = useState<MemberType | null>(null);

    const formSchema = z.object({
        name: z.string().min(1, "Name is required"),
        email: z.string().email("Invalid email"),
        phone: editData ? z.string().optional() : z.string().min(1, "Phone number is required"),
        isActive: z.boolean().optional(),
    });

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            name: "",
            email: "",
            phone: "",
            isActive: true,
        },
    });

    const { mutate: addMember, isSuccess: addMemberSuccess, data: userData } = useAddMember();
    const { mutate: editMember } = useEditMember();
    const { mutate: deleteMember } = useDeleteMember();
    const { storeData } = useOnboardingData();
    const { mutate: addPermission } = useUpdatePermission();

    const onSubmit = (data: z.infer<typeof formSchema>) => {
        if (editData) {
            editMember({
                id: editData?.id,
                store_id: storeData?.id,
                name: data.name,
                email: data.email,
                is_active: !!data.isActive,
            });

            setDialogOpen(false);
            form.reset();
            setEditData(null);
            return;
        }
        addMember({
            store_id: storeData?.id,
            name: data.name,
            email: data.email,
            phone: data.phone || "",
            is_active: !!data.isActive,
        });
        setDialogOpen(false);
        form.reset();
    };

    useEffect(() => {
        if (addMemberSuccess) {
            addPermission({
                data: [
                    {
                        module_id: "b9631bfd-a13f-40eb-b4b2-a77a13cd3a15",
                        user_id: userData?.data?.id,
                        can_get_list: true,
                        can_get_one: true,
                        can_create: false,
                        can_update: false,
                        can_delete: false,
                    },
                    {
                        module_id: "f1d86b8e-6b91-4985-b968-a09a1e8906eb",
                        user_id: userData?.data?.id,
                        can_get_list: false,
                        can_get_one: false,
                        can_create: false,
                        can_update: false,
                        can_delete: false,
                    },
                    {
                        module_id: "21b8924c-7846-41de-bfde-6cb4e20a13ed",
                        user_id: userData?.data?.id,
                        can_get_list: false,
                        can_get_one: false,
                        can_create: false,
                        can_update: false,
                        can_delete: false,
                    },
                    {
                        module_id: "71794f22-dd53-4d78-9e75-acbc54714e6c",
                        user_id: userData?.data?.id,
                        can_get_list: false,
                        can_get_one: false,
                        can_create: false,
                        can_update: false,
                        can_delete: false,
                    },
                    {
                        module_id: "4d200895-4571-4ee6-80d9-16367aceb3a4",
                        user_id: userData?.data?.id,
                        can_get_list: false,
                        can_get_one: false,
                        can_create: false,
                        can_update: false,
                        can_delete: false,
                    },
                    {
                        module_id: "de69ca63-ecae-4256-a7be-1ff17832cbce",
                        user_id: userData?.data?.id,
                        can_get_list: true,
                        can_get_one: true,
                        can_create: true,
                        can_update: true,
                        can_delete: true,
                    },
                    {
                        module_id: "d35da011-6589-4032-b14e-10f677b0013c",
                        user_id: userData?.data?.id,
                        can_get_list: false,
                        can_get_one: false,
                        can_create: false,
                        can_update: false,
                        can_delete: false,
                    },
                    {
                        module_id: "99e1ca6b-d8e2-427d-887a-eccca89d0939",
                        user_id: userData?.data?.id,
                        can_get_list: false,
                        can_get_one: false,
                        can_create: false,
                        can_update: false,
                        can_delete: false,
                    },
                    {
                        module_id: "79feddb9-6826-4ee7-8e09-b6500a4cd268",
                        user_id: userData?.data?.id,
                        can_get_list: false,
                        can_get_one: false,
                        can_create: false,
                        can_update: false,
                        can_delete: false,
                    },
                    {
                        module_id: "94a47ed9-15cd-457c-9ef4-a4c85856bfb0",
                        user_id: userData?.data?.id,
                        can_get_list: false,
                        can_get_one: false,
                        can_create: false,
                        can_update: false,
                        can_delete: false,
                    },
                    {
                        module_id: "f387835b-0570-4f77-8b39-a4f6aead0e8d",
                        user_id: userData?.data?.id,
                        can_get_list: false,
                        can_get_one: false,
                        can_create: false,
                        can_update: false,
                        can_delete: false,
                    },
                    {
                        module_id: "f2de7f69-7fcd-4ed8-a1b4-e5696327ad46",
                        user_id: userData?.data?.id,
                        can_get_list: false,
                        can_get_one: false,
                        can_create: false,
                        can_update: false,
                        can_delete: false,
                    },
                    {
                        module_id: "e9595fc9-067b-4d1e-a5b2-dcbb59409953",
                        user_id: userData?.data?.id,
                        can_get_list: true,
                        can_get_one: true,
                        can_create: true,
                        can_update: true,
                        can_delete: true,
                    },
                ],
            });
        }
    }, [addMemberSuccess]);

    const onDelete = (id: string) => {
        deleteMember({ id });
    };

    const { data: members } = useGetMembers();

    useEffect(() => {
        if (editData) {
            form.setValue("name", editData?.name);
            form.setValue("email", editData?.email);
            form.setValue("phone", editData?.phone);
            form.setValue("isActive", editData?.is_active);
        } else {
            form.reset();
        }
    }, [editData, form]);

    const { permissionList } = useGlobalPermission();
    const userPermission = permissionList.find((item) => item.module.name === "Users");

    return (
        <div>
            <Card className="mt-4 flex items-end justify-between gap-4 p-4 text-gray-700 shadow-none">
                <div className="flex gap-4">
                    <Avatar className="flex items-center justify-center border">
                        <User className="text-gray-700" />
                    </Avatar>

                    <div>
                        <h3 className="text-lg font-bold">Add a New Member</h3>
                        <p className="mt-2">Member Available on Your Account 1 of 2</p>
                    </div>
                </div>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                        <Button disabled={!userPermission?.can_create || !userPermission?.can_update}>Add Member</Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[425px]">
                        <DialogHeader>
                            <DialogTitle>{editData ? "Edit" : "Add"} Member</DialogTitle>
                            <DialogDescription>Add a new member to your account who can help manage your account.</DialogDescription>
                        </DialogHeader>
                        <Form {...form}>
                            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                                <FormField
                                    control={form.control}
                                    name="name"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Name</FormLabel>
                                            <FormControl>
                                                <Input placeholder="John Doe" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="email"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Email</FormLabel>
                                            <FormControl>
                                                <Input disabled={!!editData} placeholder="john@email.com" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                {!editData && (
                                    <FormField
                                        control={form.control}
                                        name="phone"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Phone</FormLabel>
                                                <FormControl>
                                                    <PhoneInput
                                                        className="border px-4 rounded-md py-1"
                                                        international
                                                        countryCallingCodeEditable={false}
                                                        defaultCountry="BD"
                                                        value={field.value}
                                                        onChange={field.onChange}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                )}
                                <FormField
                                    control={form.control}
                                    name="isActive"
                                    render={({ field }) => (
                                        <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 shadow">
                                            <FormControl>
                                                <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                                            </FormControl>
                                            <div className="space-y-1 leading-none">
                                                <FormLabel>Member Active</FormLabel>
                                            </div>
                                        </FormItem>
                                    )}
                                />
                                <div className="flex justify-end">
                                    <Button type="submit">Add Member</Button>
                                </div>
                            </form>
                        </Form>
                    </DialogContent>
                </Dialog>
            </Card>
            <Separator className="mt-4" />
            {members?.data?.map(
                (member: MemberType) =>
                    member?.role !== "member" && (
                        <Card key={member?.id} className="mt-4 flex items-end justify-between gap-4 p-4 text-gray-700 shadow-none">
                            <div className="flex gap-4">
                                <Avatar className="size-10">
                                    <AvatarImage src={member?.avatar} alt={member?.name} />
                                    <AvatarFallback>{member?.name?.split(" ")?.map((section: string) => section[0])}</AvatarFallback>
                                </Avatar>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h3 className="text-lg font-bold">{member?.name}</h3>
                                        <Badge className={"mt-2 text-[10px] bg-green-600"}>Store Owner</Badge>
                                    </div>
                                    <p className="mt-2">{member?.email}</p>
                                </div>
                            </div>
                        </Card>
                    )
            )}
            <Separator className="mt-4" />
            {members?.data?.map(
                (member: MemberType) =>
                    member?.role === "member" && (
                        <Card key={member?.id} className="mt-4 flex items-end justify-between gap-4 p-4 text-gray-700 shadow-none">
                            <div className="flex gap-4">
                                <Avatar className="size-10">
                                    <AvatarImage src={member?.avatar} alt={member?.name} />
                                    <AvatarFallback>{member?.name?.split(" ")?.map((section: string) => section[0])}</AvatarFallback>
                                </Avatar>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h3 className="text-lg font-bold">{member?.name}</h3>
                                        <Badge className={"mt-2 text-[10px] bg-cyan-600"}>Member</Badge>
                                    </div>
                                    <p className="mt-2">{member?.email}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <Button disabled={!userPermission?.can_delete} variant={"secondary"} onClick={() => onDelete(member?.id)}>
                                    Delete
                                </Button>
                                <Button
                                    disabled={!userPermission?.can_update}
                                    onClick={() => {
                                        setEditData(member);
                                        setDialogOpen(true);
                                    }}
                                >
                                    Edit
                                </Button>
                            </div>
                        </Card>
                    )
            )}
            {/* <Card className="mt-4 p-4 shadow-none">
                <h3>FAQ</h3>
                <Separator className="mt-4" />
                <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="item-1">
                        <AccordionTrigger className="font-bold">What can editors do?</AccordionTrigger>
                        <AccordionContent>Yes. It adheres to the WAI-ARIA design pattern.</AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="item-2">
                        <AccordionTrigger className="font-bold">What can members do?</AccordionTrigger>
                        <AccordionContent>Yes. It comes with default styles that matches the other components&apos; aesthetic.</AccordionContent>
                    </AccordionItem>
                </Accordion>
            </Card> */}
        </div>
    );
};

export default Members;
